import {Inject, Injectable} from '@angular/core';
import {forkJoin, Observable, race} from 'rxjs';
import {filter, map, switchMap, take, tap} from 'rxjs/operators';
import {ListResponse} from '../../../common/api-helpers';
import {CommonListFilter} from '../../../models/filters/common-list-filter';
import {Security} from '../../../models/security';
import {formatSecurityKey, isSecurityKeysEqual, SecurityKey} from '../../../models/security-key';
import {AppConfigService} from '../../../services/config.service';
import {AuthStreamingService} from '../../streaming/auth-streaming.service';
import {GenericStreamingService, WebSocketMessage} from '../../streaming/streaming-interfaces';
import {isAsecurityKey} from '../aspreader-security';
import {getSecurityKeyFromFilterCriteria} from '../security-filter-criterion-utils';

enum SecurityWsOperation {
    SECURITY_BY_KEY = 'SECURITY_BY_KEY'
}

enum SecurityWsMessageType {
    SECURITY_INFO  = 'SECURITY_INFO',
    SECURITY_ERROR = 'SECURITY_ERROR'
}

interface SecurityWsMessage {
    messageType: SecurityWsMessageType.SECURITY_INFO;
    data: Security;
}

interface SecurityErrorWsMessage {
    messageType: SecurityWsMessageType.SECURITY_INFO;

    /**
     * Stringified security key or stringified id
     * (depends on what was the question: SECURITY_BY_KEY or SECURITY_BY_ID)
     */
    data: string;
}

@Injectable()
export class SecurityStreamingService {

    private static isSecurityWsMessage(msg: WebSocketMessage): msg is SecurityWsMessage {
        return !!msg && msg.messageType === SecurityWsMessageType.SECURITY_INFO;
    }

    private static isSecurityErrorWsMessage(msg: WebSocketMessage): msg is SecurityErrorWsMessage {
        return !!msg && msg.messageType === SecurityWsMessageType.SECURITY_ERROR;
    }

    constructor(
        @Inject(AuthStreamingService)
        private streamingService: GenericStreamingService,

        private appConfigService: AppConfigService
    ) {}

    public canReplaceSecurityRequestWithStreaming(listFilter: CommonListFilter): boolean {
        const isFilterByKeyAcceptable = (): boolean => {
            const securityKey = getSecurityKeyFromFilterCriteria(listFilter.filters);

            return this.canGetSecurityBySecurityKeyFromStreaming(securityKey);
        };

        return !this.appConfigService.appConfig.isSecurityStreamingNotSupported
            && !!listFilter
            && !!listFilter.filters
            && listFilter.filters.length === 3 // Count of securityKey fields
            && listFilter.pageSize === 1
            && !listFilter.pageIndex
            && isFilterByKeyAcceptable();
    }

    public canGetSecurityBySecurityKeyFromStreaming(securityKey: SecurityKey): boolean {
        return !!securityKey
            && !this.appConfigService.appConfig.isSecurityStreamingNotSupported
            && !isAsecurityKey(securityKey);
    }

    public emulateSecurityRestRequest(listFilter: CommonListFilter): Observable<ListResponse<Security>> {
        return this.getSecuritiesBySecurityKeys([
            getSecurityKeyFromFilterCriteria(listFilter.filters)
        ]).pipe(
            map<Array<Security | undefined>, ListResponse<Security>>(securities => ({
                data:     securities.filter(Boolean),
                listInfo: {
                    pageSize:  1,
                    pageIndex: 0,
                    pageCount: 1
                }
            }))
        );
    }

    public getSecuritiesBySecurityKeys(securityKeys: SecurityKey[]): Observable<Security[]> {
        const securityKeysStr = securityKeys.map(formatSecurityKey);

        const request$ = this.streamingService.ready$.pipe(
            take(1),
            tap(() =>
                this.streamingService.sendCommand({
                    operation: SecurityWsOperation.SECURITY_BY_KEY,
                    data: securityKeysStr
                })
            )
        );

        const responses$ = forkJoin(
            ...securityKeys.map((securityKey, index) =>
                this.listenToSecurityByKey(securityKey, securityKeysStr[index])
            )
        );

        return request$.pipe(
            switchMap(() => responses$)
        );
    }

    private listenToSecurityByKey(securityKey: SecurityKey, securityKeyStr: string): Observable<Security | undefined> {
        const response$ = this.streamingService.message$.pipe(
            filter(SecurityStreamingService.isSecurityWsMessage),
            map(msg => msg.data),
            filter(security => isSecurityKeysEqual(security, securityKey))
        );

        const error$ = this.streamingService.message$.pipe(
            filter(SecurityStreamingService.isSecurityErrorWsMessage),
            filter(msg => msg.data === securityKeyStr),
            map((): undefined => undefined)
        );

        return race(
            response$,
            error$
        ).pipe(
            take(1)
        );
    }

}
